import { useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import sifter from "sifter";
import SearchCommand from "../../molecules/Command/SearchCommand";
import { ActivityKeyword } from "./search-types";

interface FormValues {
  activity_selection: string;
  location: string;
  mobile_activity_selection: { id: string; text: string };
  mobile_location_selection: { id: string; text: string };
}

/**
 * Reorders and caps the suggestions. Receives every keyword the query matched, in
 * sifter's own relevance order, and returns the list to show. Consumers running an
 * ordering experiment supply one; everyone else omits it and gets the default order.
 */
export type RankActivitySuggestions = (
  matches: ActivityKeyword[],
  query: string,
) => ActivityKeyword[];

const SearchActivityDropdown = ({
  setIsValidActivity,
  showLabel = true,
  commandListClasses,
  commandInputClasses,
  formName,
  commandItemClasses,
  onSelectCallback,
  keywords,
  rankSuggestions,
  shouldCloseOnBlur,
}: {
  setIsValidActivity: (isValid: { id: string; text: string }) => void;
  showLabel?: boolean;
  commandListClasses?: string;
  commandInputClasses?: string;
  formName?: keyof FormValues;
  commandItemClasses?: string;
  onSelectCallback?: () => void;
  keywords: ActivityKeyword[];
  /** Omit for the default order: best match first, `dropdown` alphabetically to break
   * ties, capped at five. */
  rankSuggestions?: RankActivitySuggestions;
  shouldCloseOnBlur?: boolean;
}) => {
  const intl = useIntl();
  const [suggestions, setSuggestions] = useState<ActivityKeyword[]>([]);

  const activityLabel = intl.formatMessage({
    id: "shared_component.mobile_activity_input.label",
    defaultMessage: "What are you planning?",
  });

  const activityPlaceholder = intl.formatMessage({
    id: "search.activity-input.placeholder",
    defaultMessage: "Enter your activity",
  });

  const sifterRef = useRef<sifter<Record<string, unknown>> | null>(null);

  useEffect(() => {
    if (keywords.length) {
      // Cast keywords array to satisfy sifter's type requirements
      sifterRef.current = new sifter(
        keywords as unknown as Record<string, ActivityKeyword>[],
      );
      // The list opens on focus, so this is what shows before a key is pressed. It goes
      // through the ranker too: without that it would differ from the identical-looking
      // state reached by typing and clearing back to an empty query.
      setSuggestions(
        (rankSuggestions ? rankSuggestions(keywords, "") : keywords).slice(
          0,
          5,
        ),
      );
    }
  }, [keywords, rankSuggestions]);

  const searchActivities = (query: string) => {
    setIsValidActivity({ id: "", text: "" });

    if (!sifterRef.current || !keywords.length) {
      setSuggestions([]);
      return;
    }

    // A ranker needs every match to order, so the cap and the tiebreak sort come off —
    // ranking a pre-sorted five would only rearrange the same five the default shows.
    const results = sifterRef.current.search(query, {
      fields: ["dropdown"],
      conjunction: "and",
      ...(rankSuggestions
        ? {}
        : { sort: [{ field: "dropdown", direction: "asc" }], limit: 5 }),
    });

    const matches = results.items
      .map((item: { id: number; score: number }) => keywords[Number(item.id)])
      .filter((item: ActivityKeyword): item is ActivityKeyword =>
        Boolean(item),
      );

    // Capped here as well as in sifter: a ranker sees an uncapped match list, and the
    // list is only ever meant to offer five.
    setSuggestions(
      (rankSuggestions ? rankSuggestions(matches, query) : matches).slice(0, 5),
    );
  };

  return (
    <SearchCommand<FormValues>
      setIsValid={setIsValidActivity}
      commandListClasses={commandListClasses}
      valueList={suggestions.map((activity) => ({
        id: activity.id,
        text: activity.dropdown,
      }))}
      formFields={{
        formName: (formName || "activity_selection") as keyof FormValues,
        placeholder: activityPlaceholder?.toString() || "",
        label: activityLabel?.toString() || "",
      }}
      filterAction={searchActivities}
      emptyMessage={
        <FormattedMessage
          id="search.activity-input.no-activity"
          defaultMessage="We don't recognize that activity. Try describing it differently"
        />
      }
      showLabel={showLabel}
      commandInputClasses={commandInputClasses}
      commandItemClasses={commandItemClasses}
      onSelectCallback={onSelectCallback}
      shouldCloseOnBlur={shouldCloseOnBlur}
    />
  );
};

export default SearchActivityDropdown;
